import { useSyncExternalStore } from 'react';
import type { Room } from 'livekit-client';

/**
 * The speaker (or headset) and microphone for calls in the app and for Patches: chosen during one
 * and kept for both, per device, in this browser (localStorage). '' is the browser's own choice.
 *
 * - Choosing a speaker needs HTMLMediaElement.setSinkId. Safari (and older browsers) don't have
 *   it: there the speaker choice isn't shown and sound plays where the system sends it.
 * - Devices are listed once a microphone has been allowed (before that, browsers hide them).
 * - A device chosen before that isn't there any more (unplugged, another computer's) is forgotten,
 *   the default is used, and the call or Patch says so.
 */

export type AudioKind = 'audioinput' | 'audiooutput';

export interface AudioDevice {
  id: string;
  label: string;
}

export interface AudioDevices {
  mics: AudioDevice[];
  /** Empty where the browser can't choose a speaker. */
  speakers: AudioDevice[];
  /** The chosen microphone and speaker ('' = the browser's default). */
  mic: string;
  speaker: string;
  canChooseSpeaker: boolean;
}

const KEYS: Record<AudioKind, string> = { audioinput: 'stitch:audio-input', audiooutput: 'stitch:audio-output' };
/** Patches kept their microphone here before calls had a choice too. */
const OLD_MIC_KEY = 'stitch:patch-mic';

/** The device chosen on this browser ('' = its default). */
export function savedDevice(kind: AudioKind): string {
  try {
    const v = localStorage.getItem(KEYS[kind]) ?? (kind === 'audioinput' ? localStorage.getItem(OLD_MIC_KEY) : null) ?? '';
    return v === 'default' ? '' : v;
  } catch {
    return '';
  }
}

function keep(kind: AudioKind, id: string): void {
  try {
    if (id) localStorage.setItem(KEYS[kind], id);
    else localStorage.removeItem(KEYS[kind]);
    if (kind === 'audioinput') localStorage.removeItem(OLD_MIC_KEY);
  } catch {
    // Not kept (a private window, storage blocked): this call or Patch still uses it.
  }
}

/** This browser can send sound to a chosen speaker or headset. */
export function canChooseSpeaker(): boolean {
  return typeof HTMLMediaElement !== 'undefined' && typeof (HTMLMediaElement.prototype as { setSinkId?: unknown }).setSinkId === 'function';
}

let state: AudioDevices = { mics: [], speakers: [], mic: savedDevice('audioinput'), speaker: savedDevice('audiooutput'), canChooseSpeaker: canChooseSpeaker() };
const listeners = new Set<() => void>();
const choosers = new Set<(kind: AudioKind, id: string) => void>();

function set(next: Partial<AudioDevices>): void {
  state = { ...state, ...next };
  for (const l of [...listeners]) l();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** The microphones and speakers, and which are chosen, for the call card and the Patch panel. */
export function useAudioDevices(): AudioDevices {
  return useSyncExternalStore(subscribe, () => state, () => state);
}

export const audioDevicesNow = (): AudioDevices => state;

/** Lists the devices again (once a microphone is allowed, they have names and ids). */
export async function refreshDevices(): Promise<AudioDevices> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return state;
  try {
    const all = await navigator.mediaDevices.enumerateDevices();
    const of = (kind: MediaDeviceKind, word: string): AudioDevice[] =>
      all.filter((d) => d.kind === kind && d.deviceId).map((d, i) => ({ id: d.deviceId, label: d.label || `${word} ${i + 1}` }));
    set({ mics: of('audioinput', 'Microphone'), speakers: canChooseSpeaker() ? of('audiooutput', 'Speaker') : [], canChooseSpeaker: canChooseSpeaker() });
  } catch {
    // No list: the defaults are used.
  }
  return state;
}

/** Chooses a device (kept for calls and Patches on this browser); a call or Patch going switches to it. */
export function chooseDevice(kind: AudioKind, id: string): void {
  const v = id === 'default' ? '' : id;
  keep(kind, v);
  set(kind === 'audioinput' ? { mic: v } : { speaker: v });
  for (const f of [...choosers]) f(kind, v);
}

/** A call or Patch hears each choice, to switch its room's device. */
export function onDeviceChosen(f: (kind: AudioKind, id: string) => void): () => void {
  choosers.add(f);
  return () => choosers.delete(f);
}

/** "The speaker you chose isn't connected, so your default one is used.", for what had to fall back. */
export function goneText(kinds: AudioKind[]): string {
  const mic = kinds.includes('audioinput');
  const speaker = kinds.includes('audiooutput');
  if (mic && speaker) return 'The speaker and microphone you chose aren’t connected, so your default ones are used.';
  if (speaker) return 'The speaker you chose isn’t connected, so your default speaker is used.';
  return 'The microphone you chose isn’t connected, so your default microphone is used.';
}

/**
 * Puts a room on the chosen devices, once its microphone is on (so the devices can be listed). A
 * chosen one that's gone is forgotten and the default used: the answer says so, in words (null when
 * nothing had to change).
 */
export async function applyChosenDevices(r: Room): Promise<string | null> {
  const now = await refreshDevices();
  const gone: AudioKind[] = [];
  const listed = (list: AudioDevice[], id: string) => list.length === 0 || list.some((d) => d.id === id);
  if (now.mic) {
    if (!listed(now.mics, now.mic)) gone.push('audioinput');
    else if (r.getActiveDevice('audioinput') !== now.mic) await r.switchActiveDevice('audioinput', now.mic).catch(() => gone.push('audioinput'));
  }
  if (now.speaker && now.canChooseSpeaker) {
    if (!listed(now.speakers, now.speaker)) gone.push('audiooutput');
    else await r.switchActiveDevice('audiooutput', now.speaker).catch(() => gone.push('audiooutput'));
  }
  for (const kind of gone) {
    keep(kind, '');
    set(kind === 'audioinput' ? { mic: '' } : { speaker: '' });
    // The microphone is on the default already (asked for the gone one, the browser gave its own).
    if (kind === 'audiooutput') await switchRoomDevice(r, 'audiooutput', '').catch(() => undefined);
  }
  return gone.length ? goneText(gone) : null;
}

/** Switches a room to a device ('' = the browser's default: setSinkId('') for a speaker). */
export function switchRoomDevice(r: Room, kind: AudioKind, id: string): Promise<unknown> {
  if (kind === 'audiooutput') return r.switchActiveDevice('audiooutput', id);
  return id ? r.switchActiveDevice('audioinput', id) : r.switchActiveDevice('audioinput', 'default', false);
}

/**
 * The devices changed (one plugged in or out) while a room is up: the lists follow, and a chosen
 * one that went away falls back to the default, with words for it (null when nothing changed).
 */
export async function devicesChanged(r: Room): Promise<string | null> {
  const before = state;
  const now = await refreshDevices();
  const gone: AudioKind[] = [];
  if (before.mic && now.mics.length && !now.mics.some((d) => d.id === before.mic)) gone.push('audioinput');
  if (before.speaker && now.canChooseSpeaker && now.speakers.length && !now.speakers.some((d) => d.id === before.speaker)) gone.push('audiooutput');
  for (const kind of gone) {
    keep(kind, '');
    set(kind === 'audioinput' ? { mic: '' } : { speaker: '' });
    await switchRoomDevice(r, kind, '').catch(() => undefined);
  }
  return gone.length ? goneText(gone) : null;
}

/** What a select shows: the devices, with the browser's default first when it doesn't list one. */
export function choices(list: AudioDevice[]): AudioDevice[] {
  return list.some((d) => d.id === 'default') ? list : [{ id: '', label: 'Default' }, ...list];
}

/** The select's value for a chosen id ('' = the default, which Chrome lists as "default"). */
export function chosenValue(list: AudioDevice[], id: string): string {
  if (id && list.some((d) => d.id === id)) return id;
  return list.some((d) => d.id === 'default') ? 'default' : '';
}
